<template>
    <div class="chat-widget">
      <button
        ref="chatToggle"
        class="chat-toggle"
        @click="toggleChat"
        :aria-label="isOpen ? 'Close chat' : 'Open chat'"
        :aria-expanded="isOpen.toString()"
        >
        💬
      </button>

  
      <div v-if="isOpen" class="chat-container">
        <div class="chat-box" ref="chatBox">
          <div
            v-for="(msg, index) in messages"
            :key="index"
            :class="['message', msg.sender]"
          >
            <span v-if="msg.sender === 'user'">{{ msg.text }}</span>
            <span v-else v-html="msg.text"></span>
          </div>
        </div>
  
        <!-- aria-live region (invisible but accessible) -->
        <div
          class="aria-live-region"
          aria-live="polite"
          aria-atomic="true"
          ref="liveRegion"
        ></div>
  
        <input
          v-model="userInput"
          @keyup.enter="sendMessage"
          placeholder="Type your message..."
        />
      </div>
    </div>
  </template>
  
  
  <script>
    export default {
  name: 'ChatWidget',
  data() {
    return {
      isOpen: false,
      userInput: '',
      messages: []
    };
  },
  created() {
    const saved = localStorage.getItem('ua_chat_messages');
    if (saved) {
      this.messages = JSON.parse(saved);
    } else {
      this.messages = [
        {
          sender: 'bot',
          text: `👋 <strong>Welcome to Universal Approach!</strong><br>Ask me about our <em>programs</em>, <em>community work</em>, or type <strong>"free"</strong> to claim a gift!`
        }
      ];
    }
  },
  mounted() {
  window.addEventListener('keydown', this.handleKeyDown);
  document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
  window.removeEventListener('keydown', this.handleKeyDown);
  document.removeEventListener('click', this.handleClickOutside);
  },
  watch: {
    messages: {
      handler(newMessages) {
        localStorage.setItem('ua_chat_messages', JSON.stringify(newMessages));
      },
      deep: true
    }
  },
  methods: {
    toggleChat() {
      this.isOpen = !this.isOpen;
      this.$nextTick(this.scrollToBottom);
    },
    handleClickOutside(event) {
    if (!this.isOpen) return;

    const chatWidget = this.$el;
    if (!chatWidget.contains(event.target)) {
      this.isOpen = false;
    }
  },
    handleKeyDown(e) {
    if (e.key === 'Escape' && this.isOpen) {
        this.isOpen = false;

        this.$nextTick(() => {
        if (this.$refs.chatToggle) {
            this.$refs.chatToggle.focus();
        }
        });
    }
    },
    sendMessage() {
      const input = this.userInput.trim();
      if (!input) return;

      this.messages.push({ sender: 'user', text: this.escapeHtml(input) });

      setTimeout(() => {
        const reply = this.getBotResponse(input);
        this.messages.push({ sender: 'bot', text: reply });

        if (this.$refs.liveRegion) {
          const plainText = reply.replace(/<[^>]+>/g, '');
          this.$refs.liveRegion.textContent = plainText;
        }

        this.scrollToBottom();
      }, 500);

      this.userInput = '';
    },
    getBotResponse(input) {
      const lower = input.toLowerCase();

      if (lower.includes('deal') || lower.includes('offer') || lower.includes('free')) {
        return `
          🎁 <strong>Limited-Time Gift!</strong><br>
          We're giving away a <span style="color:#e91e63;"><strong>FREE resource guide</strong></span>!<br><br>
          👉 <a href="mailto:universalapproachorg@gmail.com?subject=Free%20Resource%20Request" target="_blank">Click here to email us</a>.
        `;
      }

      if (lower.includes('help')) {
        return `🛟 I'm here to help. Try typing <strong>"programs"</strong>, <strong>"free"</strong>, or <strong>"contact"</strong>.`;
      }

      if (lower.includes('program') || lower.includes('services') || lower.includes('empowerment')) {
        return `
          🌱 <strong>Our Key Offerings:</strong><br>
          • Empowerment workshops<br>
          • Educational programs<br>
          • Community consulting<br><br>
          Learn more: <a href="https://universalapproach.org/service" target="_blank">About Our Service</a>
        `;
      }

      if (lower.includes('contact') || lower.includes('email') || lower.includes('reach')) {
        return `📩 Email us at <a href="mailto:universalapproachorg@gmail.com">universalapproachorg@gmail.com</a> or visit the <a href="https://universalapproach.org/#/contact" target="_blank">contact page</a>.`;
      }

      if (lower.includes('support') || lower.includes('issue') || lower.includes('question')) {
        return `🙋 Need help? Email <a href="mailto:rjannat@universalapproach.org">rjannat@universalapproach.org</a>.`;
      }

      return `🤖 I'm here to guide you. Ask about our <strong>'programs'</strong>, <strong>'offers'</strong>, or how to <strong>'contact'</strong> us.`;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatBox;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    escapeHtml(text) {
      const div = document.createElement('div');
      div.appendChild(document.createTextNode(text));
      return div.innerHTML;
    }
  }
};

  </script>
  
  <style scoped>
  .chat-widget {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 9999;
    font-family: 'Segoe UI', sans-serif;
  }
  .aria-live-region { 
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

  .chat-toggle {
    background: #42a5f5;
    color: white;
    font-weight: bold;
    font-size: 30px;
    border-radius: 50%;
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    transition: transform 0.2s ease;
    border: none;
  }
  
  .chat-toggle:focus {
    outline: 3px solid #90caf9;
    outline-offset: 2px;
  }
  
  .chat-toggle:hover {
    transform: scale(1.1);
  }
  
  .chat-container {
    width: 300px;
    max-width: 90vw;
    height: 400px;
    max-height: 80vh;
    margin-top: 10px;
    background: linear-gradient(to right, #f8fafc, #eef2f3);
    border-radius: 16px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
  }
  
  .chat-box {
    flex: 1;
    padding: 10px;
    background: white;
    overflow-y: auto;
  }
  
  .message {
    padding: 12px 16px;
    margin: 8px 0;
    max-width: 75%;
    border-radius: 18px;
    font-size: 14px;
    line-height: 1.4;
    word-wrap: break-word;
    animation: fadeIn 0.3s ease;
  }
  
  .user {
    align-self: flex-end;
    background: linear-gradient(to left, #90caf9, #42a5f5);
    color: #fff;
    border-bottom-right-radius: 4px;
  }
  
  .bot {
    align-self: flex-start;
    background: linear-gradient(to right, #a5d6a7, #66bb6a);
    color: #fff;
    border-bottom-left-radius: 4px;
  }
  
  input {
    border: none;
    padding: 12px;
    font-size: 14px;
    border-top: 1px solid #ccc;
    outline: none;
    width: 100%;
    box-sizing: border-box;
  }
  
  input:focus {
    background: #f0f0f0;
  }
  
  a {
    color: #3f51b5;
    font-weight: bold;
    text-decoration: none;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Mobile responsive */
  @media screen and (max-width: 480px) {
    .chat-widget {
      bottom: 10px;
      right: 10px;
    }
  
    .chat-container {
      width: 95vw;
      height: 60vh;
      max-height: 60vh;
      border-radius: 12px;
    }
  
    .chat-toggle {
      width: 48px;
      height: 48px;
      font-size: 20px;
    }
  
    .message {
      font-size: 13px;
      padding: 10px 14px;
    }
  
    input {
      font-size: 13px;
      padding: 10px;
    }
  }
  </style>
  
